import { useEffect, useRef, useState } from "react"

import {
  ANIMATION_SETTINGS,
  DEFAULT_VARIATIONS,
  STATE_TO_CITY_CAPITALS,
  STATE_TO_ZIP_CODES,
  getVariationsForSignal,
  pickRandomValue,
  resolveSignalKey,
} from "./signal-constants"

const STAR_REVEAL_DELAY_MS = 600

const buildFinalSeries = value => ({
  previous: value,
  current: value,
  next: value,
  upcoming: value,
})

export const useSignalSlots = (signalsData, shouldStart = false) => {
  const [slotStates, setSlotStates] = useState([])
  const [allSlotsStopped, setAllSlotsStopped] = useState(false)

  const spinIntervalsRef = useRef([])
  const animationPhaseTimeoutsRef = useRef([])
  const stopTimeoutsRef = useRef([])
  const cycleTimeoutRef = useRef(null)
  const finalConfigsRef = useRef({})
  const stoppedSlotsRef = useRef(0)
  const starRevealTimeoutRef = useRef(null)
  const hasStartedRef = useRef(false)

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const clearTimers = () => {
      spinIntervalsRef.current.forEach(id => {
        if (id) {
          window.clearInterval(id)
        }
      })
      spinIntervalsRef.current = []

      animationPhaseTimeoutsRef.current.forEach(id => {
        if (id) {
          window.clearTimeout(id)
        }
      })
      animationPhaseTimeoutsRef.current = []

      stopTimeoutsRef.current.forEach(id => {
        if (id) {
          window.clearTimeout(id)
        }
      })
      stopTimeoutsRef.current = []

      if (cycleTimeoutRef.current) {
        window.clearTimeout(cycleTimeoutRef.current)
        cycleTimeoutRef.current = null
      }

      if (starRevealTimeoutRef.current) {
        window.clearTimeout(starRevealTimeoutRef.current)
        starRevealTimeoutRef.current = null
      }
    }

    if (!signalsData.length) {
      setSlotStates([])
      setAllSlotsStopped(false)
      finalConfigsRef.current = {}
      hasStartedRef.current = false
      return clearTimers
    }

    const optionsBySlot = signalsData.map(signal =>
      getVariationsForSignal(signal.title)
    )

    if (!shouldStart) {
      if (hasStartedRef.current) {
        clearTimers()
        hasStartedRef.current = false
      }

      const initialStates = optionsBySlot.map((options, index) => {
        const pool = options?.length ? options : DEFAULT_VARIATIONS
        const current = pool[0] ?? DEFAULT_VARIATIONS[0]

        return {
          options: pool,
          previousValue: null,
          currentValue: current,
          nextValue: current,
          upcomingValue: current,
          isSpinning: false,
          phase: "resting",
          tick: 0,
          reel: [current, current, current, current],
          finalSeries: buildFinalSeries(current),
        }
      })

      setSlotStates(prev => {
        if (prev.length === 0 || prev.length !== initialStates.length) {
          return initialStates
        }
        return prev
      })

      return
    }

    if (hasStartedRef.current) {
      return
    }

    const signalKeys = signalsData.map(signal => resolveSignalKey(signal.title))

    const stateIndices = []
    const cityIndices = []
    const zipIndices = []

    signalKeys.forEach((key, index) => {
      if (key === "state") {
        stateIndices.push(index)
      } else if (key === "city") {
        cityIndices.push(index)
      } else if (key === "zip_code") {
        zipIndices.push(index)
      }
    })

    const stateToCityLink = new Map()
    const stateToZipLink = new Map()

    stateIndices.forEach((stateIndex, idx) => {
      const cityIndex = cityIndices[idx]
      if (typeof cityIndex === "number") {
        stateToCityLink.set(stateIndex, cityIndex)
      }

      const zipIndex = zipIndices[idx]
      if (typeof zipIndex === "number") {
        stateToZipLink.set(stateIndex, zipIndex)
      }
    })

    const {
      baseStopDelay,
      stopStagger,
      cycleRest,
      stepInterval,
      slotDuration,
    } = ANIMATION_SETTINGS

    const schedulePhaseReset = (index, pool) => {
      if (animationPhaseTimeoutsRef.current[index]) {
        window.clearTimeout(animationPhaseTimeoutsRef.current[index])
      }

      animationPhaseTimeoutsRef.current[index] = window.setTimeout(() => {
        setSlotStates(prev =>
          prev.map((slot, idx) => {
            if (idx !== index) {
              return slot
            }

            const currentPool =
              slot.options && slot.options.length > 0
                ? slot.options
                : pool || DEFAULT_VARIATIONS

            const safePrevious =
              slot.previousValue ??
              slot.reel?.[0] ??
              currentPool[0] ??
              DEFAULT_VARIATIONS[0]
            const safeCurrent =
              slot.currentValue ??
              slot.reel?.[1] ??
              currentPool[1 % currentPool.length] ??
              safePrevious
            const safeNext =
              slot.nextValue ??
              slot.reel?.[2] ??
              currentPool[2 % currentPool.length] ??
              safeCurrent
            const safeUpcoming =
              slot.upcomingValue ??
              slot.reel?.[3] ??
              currentPool[3 % currentPool.length] ??
              safeNext

            return {
              ...slot,
              previousValue: safePrevious,
              currentValue: safeCurrent,
              nextValue: safeNext,
              upcomingValue: safeUpcoming,
              reel: [safePrevious, safeCurrent, safeNext, safeUpcoming],
              phase: "resting",
            }
          })
        )
      }, slotDuration)
    }

    const runCycle = () => {
      clearTimers()

      const finalConfigs = {}
      stoppedSlotsRef.current = 0
      setAllSlotsStopped(false)

      stateIndices.forEach(stateIndex => {
        const pool =
          optionsBySlot[stateIndex]?.length > 0
            ? optionsBySlot[stateIndex]
            : DEFAULT_VARIATIONS

        const finalStateValue = pickRandomValue(pool, null)
        finalConfigs[stateIndex] = {
          finalValue: finalStateValue,
          finalSeries: buildFinalSeries(finalStateValue),
        }

        const cityIndex = stateToCityLink.get(stateIndex)
        if (typeof cityIndex === "number") {
          const finalCityValue =
            STATE_TO_CITY_CAPITALS[finalStateValue] ?? finalStateValue
          finalConfigs[cityIndex] = {
            finalValue: finalCityValue,
            finalSeries: buildFinalSeries(finalCityValue),
          }
        }

        const zipIndex = stateToZipLink.get(stateIndex)
        if (typeof zipIndex === "number") {
          const finalZipValue =
            STATE_TO_ZIP_CODES[finalStateValue] ?? finalStateValue
          finalConfigs[zipIndex] = {
            finalValue: finalZipValue,
            finalSeries: buildFinalSeries(finalZipValue),
          }
        }
      })

      optionsBySlot.forEach((options, index) => {
        if (finalConfigs[index]) {
          return
        }

        const pool = options?.length ? options : DEFAULT_VARIATIONS
        const finalValue = pickRandomValue(pool, null)
        finalConfigs[index] = {
          finalValue,
          finalSeries: buildFinalSeries(finalValue),
        }
      })

      finalConfigsRef.current = finalConfigs

      const initialStates = optionsBySlot.map((options, index) => {
        const pool = options?.length ? options : DEFAULT_VARIATIONS
        const previous = pickRandomValue(pool, null)
        const current = pickRandomValue(pool, previous)
        const next = pickRandomValue(pool, current)
        const upcoming = pickRandomValue(pool, next)

        const finalConfig = finalConfigsRef.current[index]

        return {
          options: pool,
          previousValue: null,
          currentValue: current,
          nextValue: next,
          upcomingValue: upcoming,
          isSpinning: true,
          phase: "resting",
          tick: 0,
          reel: [previous, current, next, upcoming],
          finalSeries: finalConfig?.finalSeries ?? buildFinalSeries(current),
        }
      })

      setSlotStates(initialStates)

      const stopOrderMap = new Map()
      signalsData.forEach((_, index) => {
        stopOrderMap.set(index, index)
      })

      optionsBySlot.forEach((options, index) => {
        if (!options.length) {
          return
        }

        const pool = options.length ? options : DEFAULT_VARIATIONS

        const spinStep = () => {
          setSlotStates(prev =>
            prev.map((slot, idx) => {
              if (idx !== index) {
                return slot
              }

              const activePool =
                slot.options && slot.options.length > 0 ? slot.options : pool

              const previousValue =
                slot.currentValue ?? activePool[0] ?? DEFAULT_VARIATIONS[0]
              const currentValue = pickRandomValue(activePool, previousValue)
              const nextValue = pickRandomValue(activePool, currentValue)
              const upcomingValue = pickRandomValue(activePool, nextValue)

              return {
                ...slot,
                previousValue,
                currentValue,
                nextValue,
                upcomingValue,
                reel: [previousValue, currentValue, nextValue, upcomingValue],
                isSpinning: true,
                phase: "animating",
                tick: (slot.tick ?? 0) + 1,
              }
            })
          )

          schedulePhaseReset(index, pool)
        }

        spinStep()

        const intervalId = window.setInterval(spinStep, stepInterval)
        spinIntervalsRef.current[index] = intervalId

        const stopOrder = stopOrderMap.get(index) ?? index
        const stopDelay = baseStopDelay + stopOrder * stopStagger

        const stopTimeoutId = window.setTimeout(() => {
          window.clearInterval(intervalId)
          spinIntervalsRef.current[index] = null

          const finalConfig = finalConfigsRef.current[index]
          const finalSeries =
            finalConfig?.finalSeries ?? buildFinalSeries(pool[0] ?? "")

          setSlotStates(prev =>
            prev.map((slot, idx) =>
              idx === index
                ? {
                    ...slot,
                    previousValue: finalSeries.previous,
                    currentValue: finalSeries.current,
                    nextValue: finalSeries.next,
                    upcomingValue: finalSeries.upcoming,
                    reel: [
                      finalSeries.previous,
                      finalSeries.current,
                      finalSeries.next,
                      finalSeries.upcoming,
                    ],
                    isSpinning: false,
                    phase: "animating",
                    tick: (slot.tick ?? 0) + 1,
                  }
                : slot
            )
          )

          stoppedSlotsRef.current += 1
          if (stoppedSlotsRef.current >= signalsData.length) {
            if (starRevealTimeoutRef.current) {
              window.clearTimeout(starRevealTimeoutRef.current)
              starRevealTimeoutRef.current = null
            }

            starRevealTimeoutRef.current = window.setTimeout(() => {
              setAllSlotsStopped(true)
            }, STAR_REVEAL_DELAY_MS)
          }

          schedulePhaseReset(index, pool)
        }, stopDelay)

        stopTimeoutsRef.current[index] = stopTimeoutId
      })

      const totalStops = signalsData.length
      const totalDuration =
        baseStopDelay + Math.max(0, totalStops - 1) * stopStagger + cycleRest

      cycleTimeoutRef.current = window.setTimeout(runCycle, totalDuration)
    }

    hasStartedRef.current = true
    runCycle()

    return () => {
      clearTimers()
      hasStartedRef.current = false
    }
  }, [signalsData, shouldStart])

  return {
    slotStates,
    allSlotsStopped,
    animationSettings: ANIMATION_SETTINGS,
  }
}
